import type {
  CommandCentreKpi,
  DomainSummary,
  ManagementAttentionResponse,
} from "@/api/intelligence";
import type { DashboardAnalyticsResponse, MonthlyBusinessPerformanceResponse } from "@/api/businessReports";
import { KpiCard } from "@/components/intelligence/KpiCard";
import { ManagementAttentionList } from "@/components/intelligence/ManagementAttentionSection";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { formatInr } from "@/lib/intelligenceFormat";
import {
  AlertTriangle,
  Building2,
  Lightbulb,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";

type Props = {
  data: MonthlyBusinessPerformanceResponse;
};

const EXECUTIVE_KPI_KEYS = [
  "gross_billing",
  "net_revenue",
  "collections",
  "outstanding",
  "new_patients",
  "opd_visits",
  "ipd_admissions",
  "leads",
] as const;

function severityClass(severity: string): string {
  switch (severity) {
    case "critical":
      return "border-l-red-600 bg-red-50/60";
    case "attention":
      return "border-l-amber-600 bg-amber-50/60";
    case "positive":
      return "border-l-emerald-600 bg-emerald-50/60";
    default:
      return "border-l-slate-400 bg-slate-50/60";
  }
}

function audienceLabel(level: MonthlyBusinessPerformanceResponse["audienceLevel"]): string {
  switch (level) {
    case "platform":
      return "Platform (all hospitals)";
    case "chain":
      return "Chain consolidated";
    default:
      return "Hospital unit";
  }
}

function DomainCard({ domain }: { domain: DomainSummary }) {
  return (
    <Card className="border-slate-200/80">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold">{domain.label}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {domain.metrics.slice(0, 4).map((m) => (
          <div key={m.key} className="flex justify-between gap-2 text-sm">
            <span className="text-muted-foreground truncate">{m.label}</span>
            <span className="font-medium tabular-nums shrink-0">
              {m.unit === "currency" ? formatInr(m.value) : m.value.toLocaleString("en-IN")}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export function MonthlyBusinessPerformanceReport({ data }: Props) {
  const kpisByKey = new Map(data.commandCentre.kpis.map((k) => [k.key, k]));
  const executiveKpis = EXECUTIVE_KPI_KEYS.map((key) => kpisByKey.get(key)).filter(
    (k): k is CommandCentreKpi => !!k
  );

  const br = data.businessReports as DashboardAnalyticsResponse;
  const rev = br.revenue.totals;
  const exp = br.expenses.totals;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-gradient-to-br from-slate-50 to-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Monthly Business Performance
            </p>
            <h2 className="text-2xl font-semibold text-slate-900 mt-1">{data.monthLabel}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Compared with {data.comparisonMonthLabel}
              {data.scope.hospitalName ? ` · ${data.scope.hospitalName}` : ""}
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1 text-xs font-medium text-slate-600">
            <Building2 className="h-3.5 w-3.5" />
            {audienceLabel(data.audienceLevel)}
          </span>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-artis-gold" />
            Executive summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm leading-relaxed text-slate-700">
            {data.executiveSummary.map((line, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-artis-gold font-bold shrink-0">•</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {data.insights.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-600" />
              Decision insights
            </CardTitle>
            <p className="text-sm text-muted-foreground font-normal">
              Prioritised actions for management based on KPI trends, expenses, and Command Centre alerts.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.insights.map((insight, i) => (
              <div
                key={i}
                className={cn("rounded-md border-l-4 p-3", severityClass(insight.severity))}
              >
                <p className="text-xs font-medium uppercase text-muted-foreground">{insight.category}</p>
                <p className="font-semibold text-slate-900 mt-0.5">{insight.title}</p>
                <p className="text-sm text-slate-600 mt-1">{insight.description}</p>
                {insight.recommendedAction && (
                  <p className="text-sm text-slate-700 mt-2">
                    <span className="font-medium">Recommended:</span> {insight.recommendedAction}
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Command Centre KPIs</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {executiveKpis.map((kpi) => (
            <KpiCard key={kpi.key} kpi={kpi} comparisonLabel={data.comparisonMonthLabel} />
          ))}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Wallet className="h-4 w-4" />
              Revenue by module
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Module</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Share</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  { label: "IPD", value: rev.ipd },
                  { label: "OPD", value: rev.opd },
                  { label: "Lab", value: rev.lab },
                  { label: "Pharma", value: rev.pharma },
                  { label: "Day Care", value: rev.dc },
                  { label: "Additional Services", value: rev.additionalServices },
                ].map((row) => (
                  <TableRow key={row.label}>
                    <TableCell>{row.label}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInr(row.value)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {rev.total > 0 ? `${((row.value / rev.total) * 100).toFixed(1)}%` : "—"}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-semibold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right">{formatInr(rev.total)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Expenses &amp; patients</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground text-xs">IPD patient expenses</p>
                <p className="text-lg font-semibold tabular-nums">{formatInr(exp.ipdPatient)}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground text-xs">Central expenses</p>
                <p className="text-lg font-semibold tabular-nums">{formatInr(exp.central)}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground text-xs">Total expenses</p>
                <p className="text-lg font-semibold tabular-nums">{formatInr(exp.total)}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-muted-foreground text-xs">OPD visits (billed)</p>
                <p className="text-lg font-semibold tabular-nums">{br.opdCount.total.toLocaleString("en-IN")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="h-4 w-4" />
              <span>
                {br.patientsBilled.totals.unique} unique patients · {br.patientsBilled.totals.repeat} repeat
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {data.pnl && (
        <Card className="border-artis-gold/30">
          <CardHeader>
            <CardTitle className="text-base">
              P&amp;L snapshot
              {data.scope.isConsolidated
                ? " (consolidated)"
                : data.scope.hospitalName
                  ? ` (${data.scope.hospitalName})`
                  : ""}
            </CardTitle>
            <p className="text-sm text-muted-foreground font-normal">
              {data.pnl.periodLabel} · matches Business Reports → P&amp;L (MoM) for this month
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">IPD count</p>
                <p className="text-xl font-semibold">{data.pnl.ipdCount}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Gross sales</p>
                <p className="text-xl font-semibold tabular-nums">{formatInr(data.pnl.grossSales)}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">Net sales</p>
                <p className="text-xl font-semibold tabular-nums">{formatInr(data.pnl.netSales)}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">EBITDA</p>
                <p className="text-xl font-semibold tabular-nums">{formatInr(data.pnl.ebitda)}</p>
              </div>
              <div className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">EBITDA margin</p>
                <p className="text-xl font-semibold tabular-nums">{data.pnl.ebitdaMarginPercent.toFixed(1)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {(br.revenueByLeadSource?.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Revenue by lead source</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Share</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {br.revenueByLeadSource.slice(0, 10).map((row) => (
                  <TableRow key={row.source}>
                    <TableCell>{row.source}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInr(row.revenue)}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.sharePercent.toFixed(1)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {data.commandCentre.domainSummaries.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Domain performance</h3>
          <div className="grid gap-3 md:grid-cols-3">
            {data.commandCentre.domainSummaries.map((d) => (
              <DomainCard key={d.domain} domain={d} />
            ))}
          </div>
        </div>
      )}

      {data.commandCentre.hospitalComparison.enabled &&
        data.commandCentre.hospitalComparison.rows.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Hospital comparison (chain)</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hospital</TableHead>
                    <TableHead className="text-right">Net revenue</TableHead>
                    <TableHead className="text-right">Collections</TableHead>
                    <TableHead className="text-right">IPD</TableHead>
                    <TableHead className="text-right">OPD</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.commandCentre.hospitalComparison.rows.map((row) => (
                    <TableRow key={row.hospitalId}>
                      <TableCell className="font-medium">{row.hospitalName}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatInr(row.netRevenue)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatInr(row.collections)}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.ipdAdmissions}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.opdVisits}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

      {data.commandCentre.managementAttention.items.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            Management attention
          </h3>
          <ManagementAttentionList
            data={data.commandCentre.managementAttention as ManagementAttentionResponse}
            compact
          />
        </div>
      )}

      {(br.surgeryRevenue?.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top surgeries by revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Surgery</TableHead>
                  <TableHead className="text-right">Admissions</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {br.surgeryRevenue.slice(0, 8).map((row) => (
                  <TableRow key={row.surgeryName}>
                    <TableCell>{row.surgeryName}</TableCell>
                    <TableCell className="text-right">{row.admissionCount}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatInr(row.revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
